import { RelationshipToSubscriber } from "@/packages/shared/src/models/coverage";
import { enforceShape } from "@/src/lib/enforce-shape";
import { logAndThrow } from "@/src/lib/logging/logger";
import { mergeNonNull } from "@/src/lib/merge-non-null";
import { withDurationMetric } from "@/src/lib/metrics";
import { customSnakeCase, customCamelCase } from "@/src/lib/string";
import { getSupabaseClient } from "@/src/lib/supabase";
import { CoverageRow } from "@/src/lib/types/tables.types";
import { Coverage, coverageShape } from "@/src/models/coverage";
import { Group } from "@/src/models/group";
import { BaseRepository } from "@/src/repo/base-repository";

export const COVERAGE_TABLE_NAME = "coverage";
export const fullCoverageSelect = `*`;

export function mapCoverageRowToCoverage(coverageRow: CoverageRow): Coverage {
  const { ...rest } = coverageRow;

  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(rest)) {
    mappedFields[customCamelCase(key)] = value;
  }

  return {
    ...mappedFields,
    relationshipToSubscriber: mappedFields.relationshipToSubscriber ?? RelationshipToSubscriber.SELF,
  } as Coverage;
}

function mapCoverageToCoverageRow(coverage: Coverage): CoverageRow {
  const { ...rest } = coverage;

  const cleanedCoverage = enforceShape(rest, coverageShape);

  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(cleanedCoverage)) {
    mappedFields[customSnakeCase(key)] = value;

    //TODO: Remove this once we have a better way to handle dates and ensure blanks are null or have a empty the date string button, FE fix?
    if (key === "effectiveDate" || key === "expirationDate" || key === "subscriberDateOfBirth") {
      mappedFields[customSnakeCase(key)] = value === "" ? null : value;
    }
  }

  return {
    ...mappedFields,
    relationship_to_subscriber: mappedFields.relationship_to_subscriber ?? RelationshipToSubscriber.SELF,
  } as CoverageRow;
}

export class CoverageRepository extends BaseRepository<Coverage, CoverageRow> {
  constructor() {
    super({
      tableName: COVERAGE_TABLE_NAME,
      selectClause: fullCoverageSelect,
      mapRowToModel: mapCoverageRowToCoverage,
      mapModelToRow: mapCoverageToCoverageRow,
    });
  }
}

export const coverageRepository = new CoverageRepository();
export const getCoverage = coverageRepository.get.bind(coverageRepository);
export const getAllCoverages = coverageRepository.getAll.bind(coverageRepository);
export const insertCoverage = coverageRepository.insert.bind(coverageRepository);
export const updateCoverage = coverageRepository.update.bind(coverageRepository);
export const deleteCoverage = coverageRepository.delete.bind(coverageRepository);

/** Save the complete set in one statement so a failed policy cannot leave a partial set. */
export async function upsertCoverages(coverages: Coverage[], group: Group): Promise<void> {
  if (coverages.length === 0) return;
  if (coverages.some((coverage) => coverage.groupId !== group.id)) {
    return logAndThrow("Coverage tenant mismatch");
  }

  const supabase = await getSupabaseClient();
  const { error } = await supabase
    .from(COVERAGE_TABLE_NAME)
    .upsert(coverages.map(mapCoverageToCoverageRow), { onConflict: "id" });

  if (error) throw error;
}

/**
 * Create or update a coverage
 * @param coverage - The coverage to create or update
 * @param overwrite - Whether to overwrite the existing coverage if it exists
 * @param group - The group to create or update the coverage in
 * @returns The created or updated coverage
 */
export const createOrUpdateCoverage = withDurationMetric(async function createOrUpdateCoverage(
  coverage: Coverage,
  overwrite: boolean = true,
  group: Group
) {
  const existingCoverage = await getCoverage(coverage.id, group);
  let coverageToReturn: Coverage | null = null;

  if (existingCoverage) {
    const mergedCoverage = mergeNonNull(existingCoverage, coverage, overwrite);

    coverageToReturn = await updateCoverage(existingCoverage.id, mergedCoverage, group);
  } else {
    coverageToReturn = await insertCoverage(coverage, group);
  }

  return coverageToReturn;
}, "createOrUpdateCoverage");
